/**
 * work-item-repository.ts
 *
 * Authoritative in-memory & localStorage-backed repository with subscription hooks.
 */

import type { WorkItem, WorkItemActivity, WorkItemRelation, WorkItemRelationType } from './work-item-contracts.js';

export type WorkItemListener = (event: { type: 'created' | 'updated' | 'archived'; item: WorkItem }) => void;

const STORAGE_KEY_ITEMS = 'sovereign_work_items_v1';
const STORAGE_KEY_ACTIVITIES = 'sovereign_work_item_activities_v1';

export class WorkItemRepository {
  private items = new Map<string, WorkItem>();
  private activities: WorkItemActivity[] = [];
  private listeners = new Set<WorkItemListener>();

  constructor() {
    this.hydrateFromStorage();
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY_ITEMS || event.key === STORAGE_KEY_ACTIVITIES) {
          this.hydrateFromStorage();
          this.notifySubscribers();
        }
      });
    }
  }

  private hydrateFromStorage(): void {
    try {
      if (typeof window === 'undefined') return;
      const rawItems = window.localStorage?.getItem(STORAGE_KEY_ITEMS);
      if (rawItems) {
        const parsed = JSON.parse(rawItems) as WorkItem[];
        if (Array.isArray(parsed)) {
          this.items.clear();
          for (const item of parsed) {
            if (item && item.id) {
              this.items.set(item.id, item);
            }
          }
        }
      }

      const rawActivities = window.localStorage?.getItem(STORAGE_KEY_ACTIVITIES);
      if (rawActivities) {
        const parsed = JSON.parse(rawActivities) as WorkItemActivity[];
        if (Array.isArray(parsed)) {
          this.activities = parsed;
        }
      }
    } catch (_err) {
      // Ignored in restricted/sandboxed iframe environments
    }
  }

  private persistToStorage(): void {
    try {
      if (typeof window === 'undefined') return;
      const itemsArray = Array.from(this.items.values());
      window.localStorage?.setItem(STORAGE_KEY_ITEMS, JSON.stringify(itemsArray));
      window.localStorage?.setItem(STORAGE_KEY_ACTIVITIES, JSON.stringify(this.activities));
      window.dispatchEvent(new CustomEvent('work-items-updated', { detail: { count: itemsArray.length } }));
    } catch (_err) {
      // Ignored in restricted/sandboxed iframe environments
    }
  }

  private notifySubscribers(): void {
    const firstItem = this.items.values().next().value;
    if (firstItem) {
      this.notify('updated', firstItem);
    }
  }

  subscribe(listener: WorkItemListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(type: 'created' | 'updated' | 'archived', item: WorkItem): void {
    for (const listener of this.listeners) {
      try {
        listener({ type, item });
      } catch (err) {
        console.error('[WorkItemRepository] Listener error:', err);
      }
    }
  }

  save(item: WorkItem): void {
    const isNew = !this.items.has(item.id);
    this.items.set(item.id, item);
    this.persistToStorage();
    this.notify(isNew ? 'created' : 'updated', item);
  }

  findById(id: string): WorkItem | undefined {
    return this.items.get(id);
  }

  findMany(predicate?: (item: WorkItem) => boolean): WorkItem[] {
    const all = Array.from(this.items.values());
    return predicate ? all.filter(predicate) : all;
  }

  archive(id: string, actorId: string): WorkItem | undefined {
    const existing = this.items.get(id);
    if (!existing) return undefined;
    const now = new Date().toISOString();
    const archived: WorkItem = {
      ...existing,
      archivedAt: now,
      updatedAt: now,
      revision: existing.revision + 1,
    };
    this.items.set(id, archived);
    this.recordActivity(id, actorId, 'archived', `Archived "${existing.title}"`);
    this.persistToStorage();
    this.notify('archived', archived);
    return archived;
  }

  recordActivity(
    workItemId: string,
    actorId: string,
    type: WorkItemActivity['type'],
    summary: string,
    contentHash: string = '',
    eventHash?: string
  ): WorkItemActivity {
    const activity: WorkItemActivity = {
      eventId: `evt-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      workItemId,
      actorId,
      type,
      summary,
      contentHash,
      payloadHash: contentHash,
      eventHash,
      createdAt: new Date().toISOString(),
    };
    this.activities.push(activity);
    this.persistToStorage();
    return activity;
  }

  getLatestEventHash(workItemId: string): string | undefined {
    const itemActs = this.activities.filter((a) => a.workItemId === workItemId);
    return itemActs.length > 0 ? itemActs[itemActs.length - 1].eventHash : undefined;
  }

  getActivities(workItemId: string): readonly WorkItemActivity[] {
    return this.activities.filter((a) => a.workItemId === workItemId);
  }

  clear(): void {
    this.items.clear();
    this.activities = [];
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(STORAGE_KEY_ITEMS);
        localStorage.removeItem(STORAGE_KEY_ACTIVITIES);
        window.dispatchEvent(new CustomEvent('work-items-updated', { detail: { count: 0 } }));
      } catch {}
    }
  }
}

export const globalWorkItemRepository = new WorkItemRepository();
